import type { DefLookup } from "../catalog";
import { findCable, findNode } from "../system-lookup";
import { diagnosticLabel } from "../i18n/diagnostic-label";
import type { Issue, System } from "../types";
import { resolveVoltageDomains } from "../voltage-domain";
import { dcPortTerminalGroups } from "../component-ports";
import { resolveTerminalPotentials } from "../terminal-voltage";
import { analyzeBatterySourceRanges, batteryConductorRegionIndex } from "../battery-stack";
import { buildGraph, v } from "./graph";

/**
 * The 12 V band spans 10–15 V to cover LiFePO4 13.6 V charge, lead 13.8 V and
 * solar absorb 14.4 V; 24 V and 48 V scale from that.
 */
const VOLTAGE_CLASS_BANDS: Array<{ nominal: number; lo: number; hi: number }> = [
  { nominal: 12, lo: 10, hi: 15 },
  { nominal: 24, lo: 20, hi: 30 },
  { nominal: 48, lo: 40, hi: 60 },
];

function voltageClass(v: number): number {
  for (const b of VOLTAGE_CLASS_BANDS) {
    if (v >= b.lo && v <= b.hi) return b.nominal;
  }
  return Math.round(v);
}

/** A declared `[12]` matches 12.8 V LFP and 13.6 V absorb, never a 24 V rail. */
function isCompatible(declared: number[], actualV: number): boolean {
  const actualClass = voltageClass(actualV);
  return declared.some((v) => voltageClass(v) === actualClass);
}

const labelOf = (
  system: System,
  defLookup: DefLookup,
  nodeId: string,
): string => {
  const node = findNode(system, nodeId);
  if (!node) return nodeId;
  if (node.kind === "zone") return node.label ?? diagnosticLabel("diag.label.zone");
  const def = defLookup(node.definitionId, node.customDefinition);
  return node.label ?? def?.shortName ?? def?.name ?? diagnosticLabel("diag.label.component");
};

/**
 * A component declaring `compatibleVoltages` on a domain that doesn't match: a
 * 12 V load on a 48 V rail dies on power-up. Opt-in — no declaration, no check.
 */
export function detectVoltageMismatch(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const issues: Issue[] = [];
  const seen = new Set<string>();
  const domains = resolveVoltageDomains(system, defLookup);
  const potentials = resolveTerminalPotentials(system, defLookup);

  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def?.compatibleVoltages || def.compatibleVoltages.length === 0) continue;

    // The positive cable's domain only: the negative would false-positive every
    // 12 V load on a shared negative bus carrying a 48 V bank's return.
    const posTermIds = new Set(
      def.terminals
        .filter((t) => t.polarity === "positive")
        .map((t) => t.id),
    );
    if (posTermIds.size === 0) continue;

    for (const c of system.cables) {
      let myTerm: string | undefined;
      if (c.fromNodeId === n.id) myTerm = c.fromTerminalId;
      else if (c.toNodeId === n.id) myTerm = c.toTerminalId;
      else continue;
      if (!posTermIds.has(myTerm)) continue;
      const dom = domains.domainByCable.get(c.id);
      if (!dom?.hasSource) continue;
      const terminal = def.terminals.find(t => t.id === myTerm)!;
      const group = dcPortTerminalGroups(def, terminal.direction).find(g => g.some(t => t.id === myTerm));
      let actualV = dom.nominalV;
      for (const negative of group?.filter(t => t.polarity !== "positive") ?? []) {
        const exact = potentials.voltageBetween(`${n.id}:${myTerm}`, `${n.id}:${negative.id}`);
        if (exact != null && exact > 0) { actualV = exact; break; }
      }
      if (isCompatible(def.compatibleVoltages, actualV)) continue;
      const key = `${n.id}:${c.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const label = labelOf(system, defLookup, n.id);
      const declared = def.compatibleVoltages
        .map((v) => `${v} V`)
        .join(" / ");
      issues.push({
        code: "voltage-mismatch",
        severity: "critical",
        title: {
          key: "diag.voltage-mismatch.title",
          vars: { label, actual: actualV, declared },
        },
        message: {
          key: "diag.voltage-mismatch.message",
          vars: { label, actual: actualV, declared },
        },
        nodeIds: [n.id],
        cableIds: [c.id],
        fix: { key: "diag.voltage-mismatch.fix" },
      });
    }
  }
  return issues;
}

/**
 * Battery source paths of different voltage class whose POSITIVE terminals land
 * on one physical rail. Compare complete strings, not their top cells. Positive-only:
 * ABYC E-11 §11.16 allows bonding every battery negative to common ground.
 */
export function detectCrossVoltageBond(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const issues: Issue[] = [];
  const graph = buildGraph(system, defLookup, { excludeSense: true });
  const sourceAnalysis = analyzeBatterySourceRanges(system, defLookup, graph);
  const sourceVoltage = new Map<string, number>();
  for (const range of sourceAnalysis.ranges) {
    const declared = range.levels.map(level => level.map(id => {
      const node = findNode(system, id);
      return node?.kind === "component" ? defLookup(node.definitionId, node.customDefinition)?.voltage : undefined;
    }));
    // Unknown source values must not establish a verified string voltage.
    if (declared.some(level => level.some(volts => volts == null || !Number.isFinite(volts) || volts <= 0))) continue;
    const lowerV = declared.slice(0, -1).reduce((sum, level) => sum + Math.max(...level as number[]), 0);
    const top = range.levels[range.levels.length - 1];
    top.forEach((id, i) => {
      // Keep each parallel member's voltage: taking the level maximum here
      // would hide a genuinely unequal pair on the same positive/return rails.
      const voltage = Math.round((lowerV + declared[declared.length - 1][i]!) * 1000) / 1000;
      sourceVoltage.set(id, Math.max(sourceVoltage.get(id) ?? 0, voltage));
    });
  }
  type BondRegion = { nodeIds: ReadonlySet<string>; terminalKeys: ReadonlySet<string>; cableIds: ReadonlySet<string> };
  const regions: BondRegion[] = [];
  if (sourceAnalysis.complete) {
    // Positive terminals must share actual copper. Synthetic series bridges
    // used for sizing domains would put every cell's positive on one rail.
    const region = batteryConductorRegionIndex(system, defLookup, graph);
    const byRegion = new Map<number, { nodeIds: Set<string>; terminalKeys: Set<string>; cableIds: Set<string> }>();
    for (const node of system.nodes) {
      if (node.kind !== "component") continue;
      const def = defLookup(node.definitionId, node.customDefinition);
      if (def?.category !== "battery") continue;
      for (const terminal of def.terminals.filter(t => t.polarity === "positive")) {
        const key = v(node.id, terminal.id), id = region(key);
        const entry = byRegion.get(id) ?? { nodeIds: new Set<string>(), terminalKeys: new Set<string>(), cableIds: new Set<string>() };
        entry.nodeIds.add(node.id);
        entry.terminalKeys.add(key);
        byRegion.set(id, entry);
      }
    }
    for (const cable of system.cables) {
      const from = region(v(cable.fromNodeId, cable.fromTerminalId));
      const to = region(v(cable.toNodeId, cable.toTerminalId));
      if (from === to) byRegion.get(from)?.cableIds.add(cable.id);
    }
    regions.push(...byRegion.values());
  } else {
    // Exhausted extraction cannot suppress an existing voltage conflict or
    // make a partial collection of paths look like a complete source model.
    regions.push(...resolveVoltageDomains(system, defLookup).domains);
  }

  for (const dom of regions) {
    type BattInfo = { nodeId: string; voltage: number; cls: number };
    const batteries: BattInfo[] = [];
    for (const nodeId of dom.nodeIds) {
      const node = system.nodes.find((n) => n.id === nodeId);
      if (!node || node.kind !== "component") continue;
      const def = defLookup(node.definitionId, node.customDefinition);
      if (!def || def.category !== "battery" || def.voltage == null) continue;
      const posTerm = def.terminals.find((t) => t.polarity === "positive");
      if (!posTerm) continue;
      if (!dom.terminalKeys.has(`${nodeId}:${posTerm.id}`)) continue;
      const voltage = sourceVoltage.get(nodeId) ?? def.voltage;
      batteries.push({ nodeId, voltage, cls: voltageClass(voltage) });
    }
    if (batteries.length < 2) continue;
    let lo: BattInfo | undefined;
    let hi: BattInfo | undefined;
    for (const b of batteries) {
      if (!lo || b.cls < lo.cls) lo = b;
      if (!hi || b.cls > hi.cls) hi = b;
    }
    if (!lo || !hi || lo.cls === hi.cls) continue;

    // A `seriesLink` is a declared stack, not a bridge to offer breaking.
    const bridgeCables: string[] = [];
    for (const cid of dom.cableIds) {
      const c = findCable(system, cid);
      if (!c) continue;
      if (c.seriesLink) continue;
      bridgeCables.push(cid);
    }
    if (bridgeCables.length === 0) continue;

    const loLabel = labelOf(system, defLookup, lo.nodeId);
    const hiLabel = labelOf(system, defLookup, hi.nodeId);
    issues.push({
      code: "cross-voltage-bond",
      severity: "critical",
      title: {
        key: "diag.cross-voltage-bond.title",
        vars: {
          fromLabel: hiLabel,
          toLabel: loLabel,
          fromV: hi.voltage,
          toV: lo.voltage,
        },
      },
      message: {
        key: "diag.cross-voltage-bond.message",
        vars: {
          fromLabel: hiLabel,
          toLabel: loLabel,
          fromV: hi.voltage,
          toV: lo.voltage,
        },
      },
      nodeIds: [hi.nodeId, lo.nodeId],
      cableIds: bridgeCables,
      fix: { key: "diag.cross-voltage-bond.fix" },
    });
  }
  return issues;
}

/**
 * IEC 61140 / 60364 extra-low-voltage touch-safety ceiling for DC. Above it,
 * special insulation, isolation and lockout requirements apply.
 */
export const SELV_DC_LIMIT_V = 60;

/**
 * A battery-bearing domain above the SELV threshold: stacking to 128 V works
 * electrically, but most terminal blocks are not insulated for hot-touch.
 */
export function detectHighVoltageRail(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const issues: Issue[] = [];
  const domains = resolveVoltageDomains(system, defLookup);
  for (const dom of domains.domains) {
    if (!dom.hasSource) continue;
    // Exactly 60 V passes: a 48 V LFP absorb hovers there and must not warn.
    if (dom.nominalV <= SELV_DC_LIMIT_V) continue;
    // A batteryless domain is a PV string or an unbuffered charger output.
    let hasBattery = false;
    let firstBatteryId: string | undefined;
    for (const nodeId of dom.nodeIds) {
      const node = system.nodes.find((n) => n.id === nodeId);
      if (!node || node.kind !== "component") continue;
      const def = defLookup(node.definitionId, node.customDefinition);
      if (def?.category === "battery") {
        hasBattery = true;
        firstBatteryId = nodeId;
        break;
      }
    }
    if (!hasBattery || !firstBatteryId) continue;
    issues.push({
      code: "high-voltage-rail",
      severity: "warning",
      title: {
        key: "diag.high-voltage-rail.title",
        vars: { actual: dom.nominalV.toFixed(0) },
      },
      message: {
        key: "diag.high-voltage-rail.message",
        vars: { actual: dom.nominalV.toFixed(0), limit: SELV_DC_LIMIT_V },
      },
      nodeIds: [firstBatteryId],
      cableIds: [...dom.cableIds],
      fix: { key: "diag.high-voltage-rail.fix" },
    });
  }
  return issues;
}

/**
 * `seriesLink` between batteries of different declared voltage: the smaller cell
 * sags below its safe floor and eventually reverses. ABYC E-11 §11.10 says stack
 * matched cells only. Tolerance is `voltageClass`, so 12.0 vs 12.8 LFP is fine.
 */
export function detectMismatchedSeriesStack(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const issues: Issue[] = [];
  const seen = new Set<string>();
  for (const c of system.cables) {
    if (!c.seriesLink) continue;
    const fromNode = system.nodes.find((n) => n.id === c.fromNodeId);
    const toNode = system.nodes.find((n) => n.id === c.toNodeId);
    if (!fromNode || !toNode) continue;
    if (fromNode.kind !== "component" || toNode.kind !== "component") continue;
    const fromDef = defLookup(fromNode.definitionId, fromNode.customDefinition);
    const toDef = defLookup(toNode.definitionId, toNode.customDefinition);
    if (!fromDef || !toDef) continue;
    if (fromDef.category !== "battery" || toDef.category !== "battery") continue;
    if (fromDef.voltage == null || toDef.voltage == null) continue;
    if (voltageClass(fromDef.voltage) === voltageClass(toDef.voltage)) continue;
    const key = [c.fromNodeId, c.toNodeId].sort().join("|");
    if (seen.has(key)) continue;
    seen.add(key);
    issues.push({
      code: "mismatched-series-stack",
      severity: "warning",
      title: {
        key: "diag.mismatched-series-stack.title",
        vars: { fromV: fromDef.voltage, toV: toDef.voltage },
      },
      message: {
        key: "diag.mismatched-series-stack.message",
        vars: { fromV: fromDef.voltage, toV: toDef.voltage },
      },
      nodeIds: [c.fromNodeId, c.toNodeId],
      cableIds: [c.id],
      fix: { key: "diag.mismatched-series-stack.fix" },
    });
  }
  return issues;
}

export function runVoltageDiagnostics(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  return [
    ...detectVoltageMismatch(system, defLookup),
    ...detectCrossVoltageBond(system, defLookup),
    ...detectHighVoltageRail(system, defLookup),
    ...detectMismatchedSeriesStack(system, defLookup),
  ];
}
